import { Participant } from "../models/Participant";
import { clean } from "../utils/utils";
import api from "./api"; // the custom Axios instance
import { Event } from "../models/Event";
import { EducationInfo } from "../models/EducationInfo";
import { EmploymentInfo } from "../models/EmploymentInfo";
import { IncomeInfo } from "../models/IncomeInfo";
import { MonthlyExpenseInfo } from "../models/MonthlyExpenseInfo";

const PATH = "/api/v1/participants";

export const ParticipantService = {
  async createParticipant(
    file: globalThis.File | null,
    participantData: Omit<
      Participant,
      "_id" | "createdAt" | "updatedAt" | "createdBy" | "updatedBy"
    >,
  ): Promise<string> {
    const formData = new FormData();
    if (file) formData.append("file", file);
    formData.append("matter", participantData.matter);
    formData.append("name", participantData.name);
    if (participantData.type) formData.append("type", participantData.type);
    if (participantData.role) formData.append("role", participantData.role);
    if (participantData.side) formData.append("side", participantData.side);
    if (participantData.details)
      formData.append("description", participantData.details);

    const response = await api.post(PATH, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data.data._id;
  },

  async getParticipant(participantId: string): Promise<Participant> {
    const response = await api.get(`${PATH}/${participantId}`);
    return response.data.data;
  },

  async getEvents(participantId: string): Promise<Event[]> {
    const res = await api.get(`${PATH}/${participantId}/events`);
    return res.data.data || [];
  },

  async updateParticipant(
    participantId: string,
    file: globalThis.File | null,
    updatedData: Partial<Omit<Participant, "_id" | "createdAt">>,
  ): Promise<void> {
    const formData = new FormData();
    if (file) formData.append("file", file);
    formData.append("matter", updatedData.matter!);
    formData.append("name", updatedData.name!);
    if (updatedData.type) formData.append("type", updatedData.type);
    if (updatedData.role) formData.append("role", updatedData.role);
    if (updatedData.side) formData.append("side", updatedData.side);
    if (updatedData.details)
      formData.append("description", updatedData.details);
    await api.put(`${PATH}/${participantId}`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  async deleteParticipant(participantId: string): Promise<void> {
    await api.delete(`${PATH}/${participantId}`);
  },

  async updateEducation(
    participantId: string,
    education: Partial<EducationInfo>,
  ): Promise<void> {
    await api.put(`${PATH}/${participantId}/education`, clean(education));
  },

  async updateEmployment(
    participantId: string,
    employment: Partial<EmploymentInfo>,
  ): Promise<void> {
    await api.put(`${PATH}/${participantId}/employment`, clean(employment));
  },

  async updateIncome(
    participantId: string,
    income: Partial<IncomeInfo>,
  ): Promise<void> {
    await api.put(`${PATH}/${participantId}/income`, clean(income));
  },

  async updateMonthlyExpenses(
    participantId: string,
    monthlyExpenses: Partial<MonthlyExpenseInfo>,
  ): Promise<void> {
    await api.put(
      `${PATH}/${participantId}/monthly-expenses`,
      clean(monthlyExpenses),
    );
  },
};
